import React, { useLayoutEffect, useRef, useState } from 'react';
import { GroupTag } from '../types/protocols';
import { BuildingGroupTags } from './BuildingGroupTags';
import './FittingGroupTags.css';

interface FittingGroupTagsProps {
    groupTags: { [key: string]: GroupTag };
}

/**
 * A building's group tags on a single line: as many as fit, then a "+n" chip
 * for the rest (its tooltip names them). The tags are measured in a hidden
 * copy of the row and re-measured when the row resizes.
 */
export const FittingGroupTags: React.FC<FittingGroupTagsProps> = ({ groupTags }) => {
    const entries = Object.entries(groupTags);
    const rowRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(entries.length);

    useLayoutEffect(() => {
        const row = rowRef.current;
        const measure = measureRef.current;
        if (!row || !measure) return;

        const compute = () => {
            const available = row.clientWidth;
            const items = Array.from(measure.children) as HTMLElement[];
            const plus = items[items.length - 1];
            const tags = items.slice(0, -1);
            // No layout (e.g. not rendered yet): show everything
            if (available === 0 || tags.length === 0) {
                setVisibleCount(tags.length);
                return;
            }
            // Each tag wrapper carries its own trailing gap, so a tag's right
            // edge in the measuring row is where the next item would start.
            let count = 0;
            for (let i = 0; i < tags.length; i++) {
                const end = tags[i].offsetLeft + tags[i].offsetWidth;
                const rest = tags.length - (i + 1);
                if (end + (rest > 0 ? plus.offsetWidth : 0) <= available) count = i + 1;
                else break;
            }
            setVisibleCount(count);
        };

        compute();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(compute);
        observer.observe(row);
        return () => observer.disconnect();
    }, [groupTags]);

    const hidden = entries.slice(visibleCount);
    const tag = ([key, value]: [string, GroupTag]) => (
        <span key={key} className="fitting-group-tags__item">
            <BuildingGroupTags groupTags={{ [key]: value }} marginBottom={0} />
        </span>
    );

    return (
        <div ref={rowRef} className="fitting-group-tags">
            {entries.slice(0, visibleCount).map(tag)}
            {hidden.length > 0 && (
                <span className="fitting-group-tags__more" title={hidden.map(([, v]) => v.tag).join(', ')}>
                    +{hidden.length}
                </span>
            )}
            {/* Hidden copy with every tag and a "+n" chip, for measuring */}
            <div ref={measureRef} className="fitting-group-tags__measure" aria-hidden="true">
                {entries.map(tag)}
                <span className="fitting-group-tags__more">+{entries.length}</span>
            </div>
        </div>
    );
};

export default FittingGroupTags;
